import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { formatDateTimeIST } from '../../utils/dateUtils';
import { formatCurrency, formatMilli } from '../../utils/formatUtils';
import { 
  Download, Calendar, Search, FileSpreadsheet, Loader2,
  ShoppingCart, Wallet, Gift, Users, ArrowUpRight, ArrowDownRight, AlertCircle,
  ChevronsUpDown, Check, Settings2
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

export const AdminClientLedgerPage = () => {
  const navigate = useNavigate();
  const [ledger, setLedger] = useState([]);
  const [totals, setTotals] = useState({});
  const [openingBalance, setOpeningBalance] = useState({});
  const [closingBalance, setClosingBalance] = useState({});
  const [periodNetChange, setPeriodNetChange] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFailed, setShowFailed] = useState(false); // Default: hide failed records
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Load users for filter dropdown
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await adminAPI.getUsers(0, 1000, '');
        setUsers(res.data.users || []);
      } catch (error) {
        console.error('Failed to load users');
      }
    };
    loadUsers();
  }, []);

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      // Pass empty string to API if 'all' is selected
      const userId = selectedUserId === 'all' ? '' : selectedUserId;
      // Pass exclude_failed based on showFailed toggle (inverted)
      const res = await adminAPI.getClientLedgerReport(userId, startDate, endDate, !showFailed);
      setLedger(res.data.ledger || []);
      setTotals(res.data.totals || {});
      setOpeningBalance(res.data.opening_balance || {});
      setClosingBalance(res.data.closing_balance || {});
      setPeriodNetChange(res.data.period_net_change || 0);
      setIsFiltered(res.data.is_filtered || false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/admin');
      } else {
        toast.error('Failed to load client ledger');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, startDate, endDate, showFailed, navigate]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const handleDownloadCSV = async () => {
    setCsvLoading(true);
    try {
      const userId = selectedUserId === 'all' ? '' : selectedUserId;
      const res = await adminAPI.downloadClientLedgerCSV(userId, startDate, endDate, !showFailed);
      
      const blob = new Blob([res.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded ${res.data.total_records} records`);
    } catch (error) {
      toast.error('Failed to download CSV');
    } finally {
      setCsvLoading(false);
    }
  };

  const getTypeIcon = (type, adjustmentType) => {
    switch (type) {
      case 'buy':
        return <ShoppingCart className="w-4 h-4 text-green-600" />;
      case 'sell':
        return <Wallet className="w-4 h-4 text-orange-600" />;
      case 'redeem':
        return <Gift className="w-4 h-4 text-purple-600" />;
      case 'referral_reward':
        return <Users className="w-4 h-4 text-blue-600" />;
      case 'gift_received':
        return <Gift className="w-4 h-4 text-green-600" />;
      case 'gift_sent':
        return <Gift className="w-4 h-4 text-orange-600" />;
      case 'admin_adjustment':
        // Green for credit, orange for debit
        return <Settings2 className={`w-4 h-4 ${adjustmentType === 'credit' ? 'text-green-600' : 'text-orange-600'}`} />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700 text-xs">Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 text-xs">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700 text-xs">Failed</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.phone?.includes(searchTerm)
  );

  // Filter ledger based on showFailed toggle
  const filteredLedger = showFailed 
    ? ledger 
    : ledger.filter(entry => entry.status !== 'failed');

  // Count failed records for display
  const failedCount = ledger.filter(entry => entry.status === 'failed').length;

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-client-ledger-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">Client Ledger Report</h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCSV}
            disabled={csvLoading || ledger.length === 0}
            className="gap-2"
          >
            {csvLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export CSV
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6 border-gold/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Filter Options
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Select User (Optional)</Label>
                <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={userDropdownOpen}
                      className="w-full justify-between font-normal"
                    >
                      {selectedUserId === 'all' 
                        ? "All Users" 
                        : users.find(u => u.user_id === selectedUserId)?.name || "Select user..."
                      }
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    <div className="p-2 border-b">
                      <Input
                        ref={searchInputRef}
                        placeholder="Search by name or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      <div
                        className="flex items-center px-2 py-2 cursor-pointer hover:bg-accent"
                        onClick={() => {
                          setSelectedUserId('all');
                          setSearchTerm('');
                          setUserDropdownOpen(false);
                        }}
                      >
                        <Check className={`mr-2 h-4 w-4 ${selectedUserId === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="font-medium">All Users</span>
                      </div>
                      {filteredUsers.slice(0, 100).map(u => (
                        <div
                          key={u.user_id}
                          className="flex items-center px-2 py-2 cursor-pointer hover:bg-accent"
                          onClick={() => {
                            setSelectedUserId(u.user_id);
                            setSearchTerm('');
                            setUserDropdownOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${selectedUserId === u.user_id ? 'opacity-100' : 'opacity-0'}`} />
                          <div className="flex flex-col">
                            <span className="text-sm">{u.name}</span>
                            <span className="text-xs text-muted-foreground">{u.phone}</span>
                          </div>
                        </div>
                      ))}
                      {filteredUsers.length === 0 && (
                        <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                          No users found
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchLedger} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Apply Filter
                </Button>
              </div>
            </div>
            
            {/* Show Failed Toggle */}
            <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-failed"
                    checked={showFailed}
                    onCheckedChange={setShowFailed}
                  />
                  <Label htmlFor="show-failed" className="cursor-pointer text-sm">
                    Show Failed Transactions
                  </Label>
                </div>
                {failedCount > 0 && (
                  <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {failedCount} failed
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {showFailed ? 'Showing all records including failed' : 'Hiding failed transactions'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="mb-4 flex items-center gap-2">
          <h3 className="text-sm font-medium text-muted-foreground">Period Summary</h3>
          {isFiltered && (
            <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 text-xs">
              Filtered
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <ArrowUpRight className="w-5 h-5 text-green-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-green-700">{formatCurrency(totals.buy_amount || 0)}</p>
              <p className="text-xs text-green-600">Bookings ({totals.buy_count || 0})</p>
              <p className="text-xs text-green-500">{(totals.buy_millis || 0).toFixed(2)} Milli's</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-4 text-center">
              <ArrowDownRight className="w-5 h-5 text-orange-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-orange-700">{formatCurrency(totals.sell_amount || 0)}</p>
              <p className="text-xs text-orange-600">Refunds ({totals.sell_count || 0})</p>
              <p className="text-xs text-orange-500">{(totals.sell_millis || 0).toFixed(2)} Milli's</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4 text-center">
              <Gift className="w-5 h-5 text-purple-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-purple-700">{(totals.redeem_millis || 0).toFixed(2)}</p>
              <p className="text-xs text-purple-600">Redeemed ({totals.redeem_count || 0})</p>
              <p className="text-xs text-purple-500">Milli's to gold</p>
            </CardContent>
          </Card>
          <Card className="border-teal-200 bg-teal-50/50">
            <CardContent className="p-4 text-center">
              <Gift className="w-5 h-5 text-teal-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-teal-700">{(totals.gift_received_millis || 0).toFixed(2)}</p>
              <p className="text-xs text-teal-600">Received ({totals.gift_received_count || 0})</p>
              <p className="text-xs text-teal-500">Gift Milli's</p>
            </CardContent>
          </Card>
          <Card className="border-pink-200 bg-pink-50/50">
            <CardContent className="p-4 text-center">
              <Gift className="w-5 h-5 text-pink-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-pink-700">{(totals.gifted_millis || 0).toFixed(2)}</p>
              <p className="text-xs text-pink-600">Sent ({totals.gifted_count || 0})</p>
              <p className="text-xs text-pink-500">Gift Milli's</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4 text-center">
              <Users className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-emerald-700">{(totals.referral_millis || 0).toFixed(2)}</p>
              <p className="text-xs text-emerald-600">Referral ({totals.referral_count || 0})</p>
              <p className="text-xs text-emerald-500">Milli's earned</p>
            </CardContent>
          </Card>
        </div>

        {/* Balance Cards - Opening + Activity = Closing */}
        <Card className="mb-6 border-navy/30 bg-gradient-to-r from-navy/5 to-gold/5">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Opening Balance */}
              <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">
                    Opening Balance {startDate ? `(Before ${startDate})` : ''}
                  </p>
                  <p className="text-lg font-bold text-gray-700">
                    {(openingBalance.millis || 0).toFixed(2)} <span className="text-sm font-normal">Milli's</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(openingBalance.value_inr || 0)}</p>
                </div>
              </div>
              
              {/* Period Net Change */}
              <div className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${periodNetChange >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  {periodNetChange >= 0 ? (
                    <ArrowUpRight className="w-5 h-5 text-green-600" />
                  ) : (
                    <ArrowDownRight className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Period Activity</p>
                  <p className={`text-lg font-bold ${periodNetChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {periodNetChange >= 0 ? '+' : ''}{periodNetChange.toFixed(2)} <span className="text-sm font-normal">Milli's</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Net change in period</p>
                </div>
              </div>
              
              {/* Closing Balance : Calculated (Actual) */}
              <div className="flex items-center gap-3 p-3 bg-navy/5 rounded-lg border border-navy/20">
                <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-navy" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">
                    Closing Balance : Calculated (Actual) {selectedUserId !== 'all' ? '' : `(${closingBalance.user_count || 0} Users)`}
                  </p>
                  <p className={`text-lg font-bold ${closingBalance.discrepancy?.has_mismatch ? 'text-red-600' : 'text-navy'}`}>
                    {(closingBalance.calculated?.millis || 0).toFixed(2)} ({(closingBalance.actual?.millis || 0).toFixed(2)}) <span className="text-sm font-normal">Milli's</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(closingBalance.calculated?.value_inr || 0)} ({formatCurrency(closingBalance.actual?.value_inr || 0)}) @ {formatCurrency(closingBalance.gold_rate_per_gram || 0)}/g
                  </p>
                  {closingBalance.discrepancy?.has_mismatch && (
                    <p className="text-xs text-red-600 font-medium mt-1">
                      ⚠️ Discrepancy: {closingBalance.discrepancy.millis > 0 ? '+' : ''}{(closingBalance.discrepancy.millis || 0).toFixed(2)} Milli's
                      {closingBalance.discrepancy.users_with_mismatch > 0 && ` (${closingBalance.discrepancy.users_with_mismatch} users)`}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Formula hint */}
            {startDate && (
              <p className="text-xs text-center text-muted-foreground mt-3 pt-3 border-t border-border/50">
                Opening ({(openingBalance.millis || 0).toFixed(2)}) + Period Activity ({periodNetChange >= 0 ? '+' : ''}{periodNetChange.toFixed(2)}) = Calculated ({(closingBalance.calculated?.millis || 0).toFixed(2)})
              </p>
            )}
          </CardContent>
        </Card>

        {/* Ledger Table */}
        <Card className="border-gold/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-navy flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Transaction Ledger
              </span>
              <span className="text-sm font-normal text-muted-foreground">
                {filteredLedger.length} records {!showFailed && failedCount > 0 && `(${failedCount} hidden)`}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredLedger.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No transactions found for the selected filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Nature</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Amount (INR)</th>
                      <th className="text-right p-3">Milli's</th>
                      <th className="text-right p-3">Rate/Milli</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLedger.map((entry, idx) => (
                      <tr key={entry.transaction_id || idx} className="border-b hover:bg-muted/30">
                        <td className="p-3 whitespace-nowrap">
                          {formatDateTimeIST(entry.date)}
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{entry.user_name}</p>
                            <p className="text-xs text-muted-foreground">{entry.user_phone}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(entry.type, entry.adjustment_type)}
                            <span>{entry.nature}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          {getStatusBadge(entry.status)}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {entry.type === 'sell' && entry.amount_inr > 0 && '-'}
                          {entry.amount_inr > 0 ? formatCurrency(entry.amount_inr) : '-'}
                        </td>
                        <td className="p-3 text-right font-mono">
                          <span className={
                            // Credits: buy, referral_reward, gift_received, admin_adjustment (credit)
                            // Debits: sell, redeem, gift_sent, admin_adjustment (debit)
                            (entry.type === 'buy' || entry.type === 'referral_reward' || entry.type === 'gift_received' || 
                             (entry.type === 'admin_adjustment' && entry.adjustment_type === 'credit'))
                              ? 'text-green-600' 
                              : 'text-orange-600'
                          }>
                            {(entry.type === 'buy' || entry.type === 'referral_reward' || entry.type === 'gift_received' ||
                              (entry.type === 'admin_adjustment' && entry.adjustment_type === 'credit')) ? '+' : '-'}
                            {entry.milli_amount?.toFixed(2)}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono text-muted-foreground">
                          {entry.rate > 0 ? `₹${entry.rate.toFixed(2)}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminClientLedgerPage;
